import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  Paper,
  useMediaQuery,
  useTheme,
  IconButton
} from '@mui/material';
import QRCode from 'react-qr-code';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import ShareIcon from '@mui/icons-material/Share';

interface QrCodeDialogProps {
  open: boolean;
  onClose: () => void;
  url: string;
  title?: string;
  onSnackbarMessage?: (message: string, severity: 'success' | 'error' | 'info' | 'warning') => void;
  className?: string;
}

const QrCodeDialog: React.FC<QrCodeDialogProps> = ({
  open,
  onClose,
  url,
  title,
  onSnackbarMessage,
  className = ''
}) => {
  const { t } = useTranslation();
  const urlInputRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Set default title with translation if not provided
  const dialogTitle = title || t('classes.qrCodeForClass');
  
  useEffect(() => {
    // Фокусируем и выделяем содержимое input-поля после открытия диалога
    if (open) {
      setTimeout(() => {
        if (urlInputRef.current) {
          urlInputRef.current.focus();
          urlInputRef.current.select();
        }
      }, 100);
    }
  }, [open]);
  
  const handleDownloadQrCode = () => {
    const svg = document.getElementById('class-qr-code');
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL('image/png');
      
      // Create download link
      const downloadLink = document.createElement('a');
      downloadLink.download = `class-qr-${className || 'code'}.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };
  
  const copyTextToClipboard = async (text: string): Promise<boolean> => {
    if (!onSnackbarMessage) return false;
    
    try {
      // Check availability of modern Clipboard API
      if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(text);
          
          // Small delay for reliability
          await new Promise(resolve => setTimeout(resolve, 300));
          
          onSnackbarMessage(t('common.linkCopied'), 'success');
          return true;
        } catch (clipboardError) {
          console.error('Clipboard API error:', clipboardError);
          // If Clipboard API caused an error, use fallback
          return fallbackCopyTextToClipboard(text);
        }
      } else {
        // Fallback for older browsers
        return fallbackCopyTextToClipboard(text);
      }
    } catch (error) {
      console.error('Failed to copy:', error);
      // Message about the problem
      onSnackbarMessage(t('common.copyFailed'), 'error');
      return false;
    }
  };

  // Fallback copy option for browsers without Clipboard API
  const fallbackCopyTextToClipboard = (text: string): boolean => {
    if (!onSnackbarMessage) return false;
    
    try {
      // Determine if mobile device is being used
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      
      if (isMobile) {
        // On mobile devices just show instructions
        onSnackbarMessage(t('common.selectAndCopy'), 'info');
        
        // Select the popup if input exists
        if (urlInputRef.current) {
          urlInputRef.current.focus();
          urlInputRef.current.select();
        }
        
        return false;
      }
      
      // For desktop use standard approach
      const tempInput = document.createElement('input');
      tempInput.style.position = 'fixed';
      tempInput.style.opacity = '0';
      tempInput.value = text;
      document.body.appendChild(tempInput);

      // Select and try to copy
      tempInput.focus();
      tempInput.select();

      // Try to use document.execCommand as a fallback
      const successful = document.execCommand('copy');
      
      // Remove temporary element
      document.body.removeChild(tempInput);
      
      if (successful) {
        onSnackbarMessage(t('common.linkCopied'), 'success');
        return true;
      } else {
        onSnackbarMessage(t('common.manualCopy'), 'warning');
        return false;
      }
    } catch (err) {
      console.error('Fallback copy failed:', err);
      onSnackbarMessage(t('common.manualCopy'), 'warning');
      return false;
    }
  };

  const handleCopyLink = async () => {    
    try {
      // Call copy function without changing button text
      await copyTextToClipboard(url);
    } catch (error) {
      console.error('Copy failed:', error);
    }
  };

  const handleShareQrCode = async () => {
    if (!onSnackbarMessage) return;
    
    if (navigator && 'share' in navigator) {
      try {
        await navigator.share({
          title: `${dialogTitle}`,
          text: t('classes.joinClassUsingLink'),
          url: url,
        });
        
        onSnackbarMessage(t('common.linkShared'), 'success');
      } catch (error) {
        console.error('Error using Web Share API:', error);
        // Fallback to clipboard if sharing fails
        await copyTextToClipboard(url);
      }
    } else {
      // Fallback for browsers that don't support sharing
      await copyTextToClipboard(url);
      onSnackbarMessage(t('common.linkCopied'), 'success');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus
      disablePortal
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {dialogTitle}
        <Button 
          size="small" 
          onClick={onClose}
          sx={{ minWidth: 'auto', p: 0.5 }}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          p: 2 
        }}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: 3, 
              mb: 3, 
              display: 'flex', 
              justifyContent: 'center',
              background: 'white' 
            }}
          >
            <QRCode
              id="class-qr-code"
              value={url}
              size={256}
              level="H"
            />
          </Paper>
          
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            width: '100%',
            gap: 1,
            mb: 2
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: 'background.paper',
              p: 1,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              width: '100%',
              '&:focus-within': {
                borderColor: 'primary.main',
                boxShadow: '0 0 0 2px rgba(43, 45, 66, 0.1)',
              },
            }}>
              <input 
                ref={urlInputRef}
                type="text"
                readOnly
                value={url}
                style={{
                  flex: 1,
                  border: 'none',
                  background: 'transparent',
                  outline: 'none',
                  padding: '8px 12px',
                  fontSize: '0.875rem',
                  fontFamily: 'inherit',
                  color: '#2B2D42',
                }}
                onClick={(e) => {
                  e.currentTarget.select();
                  // Показываем подсказку при выделении только на десктопах
                  if (onSnackbarMessage && !isMobile) {
                    onSnackbarMessage(t('classes.pressCtrlCToCopy'), 'info');
                  }
                }}
              />
              {isMobile ? (
                <IconButton
                  size="small"
                  onClick={handleCopyLink}
                  disabled={!onSnackbarMessage}
                  color="primary"
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              ) : (
                <Button
                  id="copy-button"
                  variant="outlined"
                  size="small"
                  onClick={handleCopyLink}
                  startIcon={<ContentCopyIcon />}
                  sx={{ ml: 1 }}
                  disabled={!onSnackbarMessage}
                >
                  {t('common.copy')}
                </Button>
              )}
            </Box>
            {!isMobile && (
              <Typography variant="caption" color="text.secondary" align="center">
                {t('classes.clickAndPressCtrlC')}
              </Typography>
            )}
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            width: '100%', 
            mt: 2
          }}>
            <Button 
              variant="outlined" 
              startIcon={<DownloadIcon />}
              onClick={handleDownloadQrCode}
              fullWidth
              sx={{ mr: 1 }}
            >
              {t('common.download')}
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              startIcon={<ShareIcon />}
              onClick={handleShareQrCode}
              disabled={!onSnackbarMessage}
              fullWidth
            >
              {t('common.share')}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default QrCodeDialog; 